/*
 * Read-only agent tools. Results are plain text for the model; every piece of
 * event text is wrapped in <event_text> delimiters (untrusted data).
 */

import { validateEventGroup } from "../../../external/cc-lib/dist/lib/styleGuideTests.js";
import { resolveGroup } from "./guards.js";
import {
    MAX_RESULT_CHARS,
    MAX_RESULT_EVENTS,
    clip,
    roundTime,
    toMarkup,
    toPlain,
    wrapEventText,
} from "./text.js";

const GUIDELINE_KEYS = ["maxCps", "maxWpm", "maxChars", "maxLines", "minDuration", "maxDuration"];

function timing(ctx, event) {
    const start = roundTime(event.start);
    const end = roundTime(event.end);
    const tc = (value) => (value === null ? "unset" : ctx.formatTc(value));
    return `${start ?? "unset"}-${end ?? "unset"}s (${tc(start)} - ${tc(end)})`;
}

function eventLine(ctx, event, index) {
    const parts = [`#${index + 1} ${timing(ctx, event)}`];
    const speakers = (event.speakers ?? []).map((speaker) => speaker?.name).filter(Boolean);
    if (speakers.length) parts.push(`speakers: ${clip(speakers.join(", "), 120)}`);
    const tags = (event.tags ?? []).map((tag) => (typeof tag === "string" ? tag : tag?.term ?? tag?.name)).filter(Boolean);
    if (tags.length) parts.push(`tags: ${clip(tags.join(", "), 120)}`);
    if (event.approved === false) parts.push("unapproved");
    if (event.notes) parts.push(`notes: ${wrapEventText(clip(event.notes, 200))}`);
    parts.push(wrapEventText(toMarkup(event.text)));
    return parts.join(" | ");
}

/** Render a capped list of events; stops at the event or character budget. */
function renderEvents(ctx, group, indexes, header) {
    const lines = [header];
    let chars = header.length;
    let shown = 0;
    for (const index of indexes) {
        if (shown >= MAX_RESULT_EVENTS) break;
        const line = eventLine(ctx, group.events[index], index);
        if (chars + line.length > MAX_RESULT_CHARS && shown > 0) break;
        lines.push(line);
        chars += line.length + 1;
        shown++;
    }
    if (shown < indexes.length) {
        const next = indexes[shown] + 1;
        lines.push(`… ${indexes.length - shown} more not shown (continue from event #${next}).`);
    }
    if (indexes.length === 0) lines.push("No events.");
    return lines.join("\n");
}

function groupHeader(group, index) {
    return `Group ${index + 1} "${clip(group.name, 120)}"`;
}

function getProjectSummary(_input, ctx) {
    const project = ctx.getProject();
    const groups = ctx.getGroups();
    const lines = [
        `Project: ${wrapEventText(clip(project.name ?? "", 200))}`,
        `Frame rate: ${project.frameRate}${project.dropFrame ? " (drop frame)" : ""}; incode ${ctx.formatTc(0)}`,
        `Player position: ${roundTime(ctx.getPlayerTime())}s (${ctx.formatTc(ctx.getPlayerTime() || 0)})`,
        `Event Groups (${groups.length}):`,
    ];
    groups.forEach((group, index) => {
        const guide = GUIDELINE_KEYS.filter((key) => group[key] !== undefined && group[key] !== null)
            .map((key) => `${key} ${group[key]}`)
            .join(", ");
        lines.push(
            `${index + 1}. ${wrapEventText(clip(group.name, 120))} - type ${group.type}; language ${group.language}` +
                `${group.rtl ? " (RTL)" : ""}; ${group.events?.length ?? 0} events; locked: ${ctx.isLocked(index) ? "yes" : "no"}` +
                `${guide ? `; guidelines: ${guide}` : ""}${project.selected === index ? " (selected)" : ""}`,
        );
    });
    const selectedGroup = groups[project.selected];
    if (selectedGroup && selectedGroup.selected?.length) {
        const selected = selectedGroup.selected.filter((i) => selectedGroup.events?.[i]).slice(0, 10);
        lines.push(`Selected events in group ${project.selected + 1}:`);
        selected.forEach((i) => lines.push(eventLine(ctx, selectedGroup.events[i], i)));
    }
    return { content: lines.join("\n"), activity: { kind: "read_project" } };
}

function getEvents(input, ctx) {
    const resolved = resolveGroup(ctx, input.group);
    if (resolved.error) return { error: resolved.error };
    const { group, index } = resolved;
    const count = group.events?.length ?? 0;
    const from = Math.max(1, input.from ?? 1);
    const to = Math.min(count, input.to ?? count);
    const indexes = [];
    for (let i = from - 1; i < to; i++) {
        const event = group.events[i];
        if (input.start_time !== undefined && !(event.end > input.start_time)) continue;
        if (input.end_time !== undefined && !(event.start < input.end_time)) continue;
        indexes.push(i);
    }
    const header = `${groupHeader(group, index)}: ${indexes.length} of ${count} events match. Times are seconds from the start of the media; timecodes include the incode.`;
    return {
        content: renderEvents(ctx, group, indexes, header),
        activity: { kind: "read_events", groupIndex: index, groupName: group.name, count: indexes.length },
    };
}

function speakerNames(event) {
    return (event.speakers ?? []).map((speaker) => String(speaker?.name ?? "").toLowerCase());
}

function tagNames(event) {
    return (event.tags ?? []).map((tag) => String(typeof tag === "string" ? tag : tag?.term ?? tag?.name ?? "").toLowerCase());
}

function searchEvents(input, ctx) {
    const resolved = resolveGroup(ctx, input.group);
    if (resolved.error) return { error: resolved.error };
    const { group, index } = resolved;

    let matcher = null;
    if (input.text !== undefined && input.text !== "") {
        if (input.regex) {
            try {
                matcher = new RegExp(input.text, input.case_sensitive ? "u" : "iu");
            } catch (error) {
                return { error: `Invalid regular expression: ${error.message}` };
            }
        } else {
            const needle = input.case_sensitive ? input.text : input.text.toLowerCase();
            matcher = { test: (value) => (input.case_sensitive ? value : value.toLowerCase()).includes(needle) };
        }
    }
    const speaker = input.speaker?.toLowerCase();
    const tag = input.tag?.toLowerCase();

    const indexes = [];
    (group.events ?? []).forEach((event, i) => {
        const plain = toPlain(event.text);
        if (input.empty === true && plain.trim() !== "") return;
        if (input.empty === false && plain.trim() === "") return;
        if (matcher && !matcher.test(plain)) return;
        if (speaker && !speakerNames(event).includes(speaker)) return;
        if (tag && !tagNames(event).includes(tag)) return;
        indexes.push(i);
    });
    const header = `${groupHeader(group, index)}: ${indexes.length} matching events.`;
    return {
        content: renderEvents(ctx, group, indexes, header),
        activity: { kind: "search", groupIndex: index, groupName: group.name, count: indexes.length },
    };
}

const CHECK_CODES = {
    reading_speed: ["cpsMax", "wpm"],
    overlap: ["overlap"],
    duration: ["durationMin", "durationMax"],
    line_length: ["maxLines", "maxCharsPerLine"],
};

function getQcIssues(input, ctx) {
    const resolved = resolveGroup(ctx, input.group);
    if (resolved.error) return { error: resolved.error };
    const { group, index } = resolved;
    const project = ctx.getProject();
    const checks = input.checks?.length ? [...new Set(input.checks)] : Object.keys(CHECK_CODES);
    const codes = new Set(checks.flatMap((check) => CHECK_CODES[check]));

    const styleGuide = {
        maxLines: group.maxLines,
        maxChars: group.maxChars,
        minDuration: group.minDuration,
        maxDuration: group.maxDuration,
        maxCps: group.maxCps,
        maxWpm: group.maxWpm,
        overlap: true,
    };
    const errors = validateEventGroup(group, styleGuide, {
        frameRate: project.frameRate,
        dropFrame: project.dropFrame,
        excludeSpaces: ctx.excludeSpaces,
    });

    const counts = Object.fromEntries(checks.map((check) => [check, 0]));
    const flagged = [];
    const issuesByIndex = new Map();
    for (const error of errors) {
        const issues = (error.issues ?? []).filter((issue) => codes.has(issue.code));
        if (!issues.length) continue;
        const eventIndex = error.index - 1;
        flagged.push(eventIndex);
        issuesByIndex.set(eventIndex, issues.map((issue) => issue.message));
        for (const check of checks) {
            if (issues.some((issue) => CHECK_CODES[check].includes(issue.code))) counts[check]++;
        }
    }

    const summary = checks.map((check) => `${check}: ${counts[check]}`).join(", ");
    const lines = [
        `${groupHeader(group, index)} QC against its guidelines (${GUIDELINE_KEYS.map((key) => `${key} ${group[key]}`).join(", ")}).`,
        `Events with issues - ${summary}.`,
    ];
    let chars = lines.join("\n").length;
    let shown = 0;
    for (const eventIndex of flagged) {
        if (shown >= MAX_RESULT_EVENTS) break;
        const line = `${eventLine(ctx, group.events[eventIndex], eventIndex)} | issues: ${issuesByIndex.get(eventIndex).join("; ")}`;
        if (chars + line.length > MAX_RESULT_CHARS && shown > 0) break;
        lines.push(line);
        chars += line.length + 1;
        shown++;
    }
    if (shown < flagged.length) lines.push(`… ${flagged.length - shown} more events with issues not shown.`);
    return {
        content: lines.join("\n"),
        activity: { kind: "qc", groupIndex: index, groupName: group.name, count: flagged.length },
    };
}

export const READ_TOOLS = {
    get_project_summary: getProjectSummary,
    get_events: getEvents,
    search_events: searchEvents,
    get_qc_issues: getQcIssues,
};
